import {
  Alert,
  Badge,
  Group,
  Loader,
  MultiSelect,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOsAllocationQueries } from '../../lib/queries/os-allocation';

import { Permission, usePermissions } from '@auth/data-access';
import { dashboardQueries } from '@dashboard';
import { useQueriesWithGlobal } from '@data-access';
import { FullMainHeight } from '@ui';

const numberFormat = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const percentFormat = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

function formatHa(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${numberFormat.format(value)} ha`;
}

function formatTonnes(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${numberFormat.format(value)} t`;
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return '-';
  }
  return `${percentFormat.format(value * 100)} %`;
}

export function OsAllocationPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const showOwnerFilter = can(Permission.DashboardOwnerFilter);
  const [organizationIds, setOrganizationIds] = useState<number[]>([]);
  const [ownerIds, setOwnerIds] = useState<number[]>([]);
  const [contractId, setContractId] = useState<number | null>(null);
  const osAllocationQueries = useOsAllocationQueries();
  const sharedQueries = useQueriesWithGlobal(dashboardQueries);

  const organizationsQuery = useQuery(sharedQueries.organisations());
  const organizationOptions = useMemo(
    () =>
      (organizationsQuery.data?.results ?? []).map((organization) => ({
        value: String(organization.id),
        label: organization.name,
      })),
    [organizationsQuery.data?.results],
  );

  const allocationQuery = useQuery({
    ...osAllocationQueries.simulation({
      organizationIds,
      ownerIds: showOwnerFilter ? ownerIds : [],
      contractId,
    }),
    placeholderData: keepPreviousData,
  });

  const ownerOptions = useMemo(
    () =>
      (allocationQuery.data?.filters.owners ?? []).map((owner) => ({
        value: String(owner.id),
        label: owner.name,
      })),
    [allocationQuery.data?.filters.owners],
  );

  useEffect(() => {
    setOwnerIds((current) =>
      current.filter((ownerId) =>
        ownerOptions.some((option) => Number(option.value) === ownerId),
      ),
    );
  }, [ownerOptions]);

  const contractOptions = useMemo(
    () =>
      (allocationQuery.data?.filters.contracts ?? []).map((contract) => ({
        value: String(contract.id),
        label: contract.name,
      })),
    [allocationQuery.data?.filters.contracts],
  );

  useEffect(() => {
    if (
      contractId !== null &&
      !contractOptions.some((option) => Number(option.value) === contractId)
    ) {
      setContractId(null);
    }
  }, [contractId, contractOptions]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Stack gap="md">
            <Group justify="space-between" wrap="wrap">
              <Title order={2}>{t('app.nav.osAllocation')}</Title>
              {allocationQuery.data?.campaign_years?.length ? (
                <Badge size="lg" variant="light">
                  {t('osAllocation.allCampaignsCount', {
                    count: allocationQuery.data.campaign_years.length,
                  })}
                </Badge>
              ) : null}
            </Group>
            <Group align="flex-end" wrap="wrap">
              <MultiSelect
                data={organizationOptions}
                label={t('common.organisation')}
                onChange={(values) => setOrganizationIds(values.map((value) => Number(value)))}
                searchable
                value={organizationIds.map(String)}
                w={320}
              />
              {showOwnerFilter ? (
                <MultiSelect
                  clearable
                  data={ownerOptions}
                  label={t('statistics.owner-filter')}
                  onChange={(values) => setOwnerIds(values.map((value) => Number(value)))}
                  placeholder={t('osAllocation.allOwners')}
                  searchable
                  value={ownerIds.map(String)}
                  w={280}
                />
              ) : null}
              <Select
                clearable
                data={contractOptions}
                label={t('app.nav.contracts')}
                onChange={(value) => setContractId(value ? Number(value) : null)}
                placeholder={t('osAllocation.allContracts')}
                searchable
                value={contractId ? String(contractId) : null}
                w={320}
              />
            </Group>
          </Stack>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <ScrollArea h="100%" offsetScrollbars type="auto">
          <Stack gap="md" pb="md">
            {allocationQuery.isPending && !allocationQuery.data ? (
              <Paper p="lg" withBorder>
                <Group>
                  <Loader size="sm" />
                  <Text>{t('osAllocation.loading')}</Text>
                </Group>
              </Paper>
            ) : null}

            {allocationQuery.isError ? (
              <Alert color="red" title={t('common.error.title', { defaultValue: 'Error' })}>
                {t('osAllocation.loadError')}
              </Alert>
            ) : null}

            {allocationQuery.data ? (
              <>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
                  <Paper p="md" withBorder>
                    <Text c="dimmed" size="sm">
                      {t('osAllocation.summary.uniqueArea')}
                    </Text>
                    <Text fw={700} size="xl">
                      {formatHa(allocationQuery.data.summary.unique_area_ha)}
                    </Text>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Text c="dimmed" size="sm">
                      {t('osAllocation.summary.rawArea')}
                    </Text>
                    <Text fw={700} size="xl">
                      {formatHa(allocationQuery.data.summary.raw_area_ha)}
                    </Text>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Text c="dimmed" size="sm">
                      {t('osAllocation.summary.overlapArea')}
                    </Text>
                    <Text fw={700} size="xl">
                      {formatHa(allocationQuery.data.summary.overlap_area_ha)}
                    </Text>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Text c="dimmed" size="sm">
                      {t('osAllocation.summary.geometricOverlapArea')}
                    </Text>
                    <Text fw={700} size="xl">
                      {formatHa(allocationQuery.data.summary.geometric_overlap_area_ha)}
                    </Text>
                  </Paper>
                  <Paper p="md" withBorder>
                    <Text c="dimmed" size="sm">
                      {t('osAllocation.summary.selections')}
                    </Text>
                    <Text fw={700} size="xl">
                      {numberFormat.format(allocationQuery.data.summary.selection_count)}
                    </Text>
                  </Paper>
                </SimpleGrid>

                {allocationQuery.data.summary.overlapping_selection_count > 0 ? (
                  <Alert color="blue" variant="light">
                    {t('osAllocation.summary.overlappingSelections')}:{' '}
                    {numberFormat.format(allocationQuery.data.summary.overlapping_selection_count)}
                  </Alert>
                ) : null}

                {allocationQuery.data.warnings.length > 0 ? (
                  <Alert color="yellow" title={t('osAllocation.warningsTitle')}>
                    <Stack gap={4}>
                      {allocationQuery.data.warnings.map((warning) => (
                        <Text key={warning} size="sm">
                          {warning}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                ) : null}

                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Title order={3}>{t('osAllocation.sections.owners')}</Title>
                    <Table striped withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t('statistics.owner-filter')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.volume')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.surface')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.share')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.pratiques')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {allocationQuery.data.owners.map((owner) => (
                          <Table.Tr key={owner.id}>
                            <Table.Td>{owner.name}</Table.Td>
                            <Table.Td>{formatTonnes(owner.volume_t)}</Table.Td>
                            <Table.Td>{formatHa(owner.allocated_surface_ha)}</Table.Td>
                            <Table.Td>{formatPercent(owner.share_ratio)}</Table.Td>
                            <Table.Td>{numberFormat.format(owner.pratique_count)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Paper>

                <Paper p="md" withBorder>
                  <Stack gap="sm">
                    <Title order={3}>{t('osAllocation.sections.deduplication')}</Title>
                    <Text c="dimmed" size="sm">
                      {allocationQuery.data.deduplication.scope === 'contract'
                        ? t('osAllocation.deduplicationScopeContract')
                        : t('osAllocation.deduplicationScopeOrganization')}
                    </Text>
                    <Text size="sm">{t('osAllocation.deduplicationIntro')}</Text>

                    <Table striped withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t('osAllocation.columns.metric')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.durable')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.geometric')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          <Table.Td>{t('osAllocation.deduplicationRows.raw')}</Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.raw_durable_ha)}
                          </Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.raw_geometric_ha)}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td>{t('osAllocation.deduplicationRows.unique')}</Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.unique_durable_ha)}
                          </Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.unique_geometric_ha)}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr>
                          <Table.Td>{t('osAllocation.deduplicationRows.overlap')}</Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.overlap_durable_ha)}
                          </Table.Td>
                          <Table.Td>
                            {formatHa(allocationQuery.data.deduplication.overlap_geometric_ha)}
                          </Table.Td>
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>

                    <Title order={4}>{t('common.campaign')}</Title>
                    <Table striped withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t('common.campaign')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.selections')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.durable')}</Table.Th>
                          <Table.Th>{t('osAllocation.columns.geometric')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {allocationQuery.data.deduplication.selections_by_year.map((row) => (
                          <Table.Tr key={row.year}>
                            <Table.Td>{row.year}</Table.Td>
                            <Table.Td>{numberFormat.format(row.field_count)}</Table.Td>
                            <Table.Td>{formatHa(row.durable_ha)}</Table.Td>
                            <Table.Td>{formatHa(row.geometric_ha)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Stack>
                </Paper>
              </>
            ) : null}
          </Stack>
        </ScrollArea>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default OsAllocationPage;
